import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PackageIcon, XIcon, LoaderIcon } from 'lucide-react';
import { useEmail } from '../contexts/EmailContext';
import { loadRentalsToLocalStorage } from '../utils/helper';
interface RentalRequest {
  id: string;
  date: string;
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    shippingLocation?: string;
    eventName?: string;
  };
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
  };
  total: number;
  subtotal: number;
  tax: number;
}
export function PendingRentals({
  refreshKey,
  shouldExpand = false,
  onExpandChange
}: {
  refreshKey?: number;
  shouldExpand?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}) {
  const {
    email,
    setEmail
  } = useEmail();
  const [isExpanded, setIsExpanded] = useState(false);
  const [rentals, setRentals] = useState<RentalRequest[]>([]);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoadingRentals, setIsLoadingRentals] = useState(false);
  const previousRefreshKey = useRef<number | undefined>(undefined);
  // Handle shouldExpand prop changes
  useEffect(() => {
    if (shouldExpand && email) {
      setIsExpanded(true);
      // Reset the shouldExpand flag
      if (onExpandChange) {
        onExpandChange(false);
      }
    }
  }, [shouldExpand, email, onExpandChange]);
  useEffect(() => {
    if (previousRefreshKey.current === undefined) {
      // on INIT
      localStorage.setItem('pendingRentals', '');
    } else if (previousRefreshKey.current !== refreshKey) {
      // on Refresh
      const savedRentals = localStorage.getItem('pendingRentals');
      if (savedRentals) {
        setRentals(JSON.parse(savedRentals));
      }
    }
    previousRefreshKey.current = refreshKey;
  }, [refreshKey]);
  const handleToggleExpand = () => {
    if (!email) {
      // Show email popup if email is not set
      setShowEmailPopup(true);
      setTempEmail('');
      setEmailError('');
    } else {
      // Toggle expansion if email is already set
      setIsExpanded(!isExpanded);
    }
  };
  const handleEmailSubmit = async () => {
    // Validate email
    if (!tempEmail.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(tempEmail)) {
      setEmailError('Please enter a valid email');
      return;
    }
    setIsLoadingRentals(true);
    try {
      // load rentals from zoho creator, save them to local storage, then call setRentals
      await loadRentalsToLocalStorage(tempEmail.toLowerCase());
      const savedRentals = localStorage.getItem('pendingRentals');
      if (savedRentals) {
        setRentals(JSON.parse(savedRentals));
      }
      setEmail(tempEmail);
      setShowEmailPopup(false);
      setIsExpanded(true);
    } catch (error) {
      console.error('Error loading rentals:', error);
      setEmailError('Failed to load rentals. Please try again.');
    } finally {
      setIsLoadingRentals(false);
    }
  };
  const handleClosePopup = () => {
    setShowEmailPopup(false);
    setTempEmail('');
    setEmailError('');
  };
  return <>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-6" data-id="element-401">
        <button onClick={handleToggleExpand} className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-402">
          <div className="flex items-center" data-id="element-403">
            <PackageIcon size={20} className="text-blue-600 mr-3" data-id="element-404" />
            <div className="text-left" data-id="element-405">
              <h3 className="font-semibold text-slate-800" data-id="element-406">Pending Rentals</h3>
              {email && <p className="text-sm text-slate-500" data-id="element-407">
                  {rentals.length} rental request
                  {rentals.length !== 1 ? 's' : ''} submitted
                </p>}
              {!email && <p className="text-sm text-slate-500" data-id="element-408">
                  Click to view your rentals
                </p>}
            </div>
          </div>
          {isExpanded ? <ChevronUpIcon size={20} className="text-slate-400" data-id="element-409" /> : <ChevronDownIcon size={20} className="text-slate-400" data-id="element-410" />}
        </button>
        {isExpanded && rentals.length > 0 && <div className="border-t border-slate-200 px-6 py-4" data-id="element-411">
            <div className="space-y-4" data-id="element-412">
              {rentals.map(rental => <div key={rental.id} className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-413">
                  <div className="flex justify-between items-start mb-3" data-id="element-414">
                    <div data-id="element-415">
                      <h4 className="font-medium text-slate-800" data-id="element-416">
                        {rental.customerInfo.name}
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-417">
                        {new Date(rental.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-418">
                      <p className="font-semibold text-slate-800" data-id="element-419">
                        ${rental.total.toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-420">Total</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-421">
                    {rental.selections.fullGen3Ring > 0 && <div className="text-slate-600" data-id="element-422">
                        Full Gen 3 Ring: {rental.selections.fullGen3Ring}
                      </div>}
                    {rental.selections.gen3Body > 0 && <div className="text-slate-600" data-id="element-423">
                        Gen 3 Body: {rental.selections.gen3Body}
                      </div>}
                    {rental.selections.gen3Head > 0 && <div className="text-slate-600" data-id="element-424">
                        Gen 3 Head: {rental.selections.gen3Head}
                      </div>}
                    {rental.selections.gen3JudgesOnly > 0 && <div className="text-slate-600" data-id="element-425">
                        Judges Only: {rental.selections.gen3JudgesOnly}
                      </div>}
                    {rental.selections.requireTech && <div className="text-slate-600 col-span-2" data-id="element-426">
                        ✓ Technician Required
                      </div>}
                  </div>
                  {rental.customerInfo.eventName && <div className="mt-2 text-sm text-slate-600" data-id="element-427">
                      <span className="font-medium" data-id="element-428">Event:</span>{' '}
                      {rental.customerInfo.eventName}
                    </div>}
                </div>)}
            </div>
          </div>}
        {isExpanded && rentals.length === 0 && <div className="border-t border-slate-200 px-6 py-8 text-center text-slate-500" data-id="element-429">
            No pending rentals yet
          </div>}
      </div>
      {/* Email Popup */}
      {showEmailPopup && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-430">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-431">
            <div className="flex justify-between items-center p-6 border-b border-slate-200" data-id="element-432">
              <h3 className="text-lg font-semibold text-slate-800" data-id="element-433">
                Enter Your Email
              </h3>
              <button onClick={handleClosePopup} className="text-slate-400 hover:text-slate-600" disabled={isLoadingRentals} data-id="element-434">
                <XIcon size={20} data-id="element-435" />
              </button>
            </div>
            <div className="p-6" data-id="element-436">
              <p className="text-slate-600 mb-4" data-id="element-437">
                Please provide your email address to view your pending rentals.
              </p>
              <div data-id="element-438">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-439">
                  Email Address
                </label>
                <input type="email" value={tempEmail} onChange={e => {
              setTempEmail(e.target.value);
              setEmailError('');
            }} onKeyPress={e => {
              if (e.key === 'Enter' && !isLoadingRentals) {
                handleEmailSubmit();
              }
            }} className={`w-full px-3 py-2 border rounded-md ${emailError ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="your.email@example.com" autoFocus disabled={isLoadingRentals} data-id="element-440" />
                {emailError && <p className="text-red-500 text-xs mt-1" data-id="element-441">{emailError}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200" data-id="element-442">
              <button onClick={handleClosePopup} className="px-4 py-2 text-slate-600 hover:text-slate-800" disabled={isLoadingRentals} data-id="element-443">
                Cancel
              </button>
              <button onClick={handleEmailSubmit} disabled={isLoadingRentals} className={`px-4 py-2 rounded-md flex items-center ${isLoadingRentals ? 'bg-blue-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-444">
                {isLoadingRentals ? <>
                    <LoaderIcon size={16} className="mr-2 animate-spin" data-id="element-445" />
                    Loading...
                  </> : 'Continue'}
              </button>
            </div>
          </div>
        </div>}
    </>;
}